<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  MEMBER_COMPANY_TOKEN,
  MEMBER_SCOPE_TOKENS,
  memberRestrictionRule,
} from 'dashboard/routes/dashboard/appBuilder/sectionTemplates';

const props = defineProps({
  block: { type: Object, required: true },
  dataSources: { type: Array, default: () => [] },
  metadataFields: { type: Array, default: () => [] },
  memberGroups: { type: Array, default: () => [] },
});

const emit = defineEmits(['update', 'updateDataSource', 'createDataSource']);

const { t } = useI18n();

// The server-enforced moat tokens and rule builder live in sectionTemplates.js (single source
// of truth, shared with the Sections palette). A data source carrying a contact_id rule with one
// of these tokens scopes every row to the authenticated member at runtime (RowRestrictionResolver).
// Without it the runtime gateway fails closed (MissingMemberBindingError -> 422), so configuring it
// here is mandatory for any member-scoped entity app.

const selectedDataSource = computed(() =>
  props.dataSources.find(ds => ds.key === props.block.data_source_key)
);

function ruleBindsCurrentMember(rule) {
  return (
    rule &&
    rule.attribute_key === 'contact_id' &&
    Array.isArray(rule.values) &&
    rule.values.some(v => MEMBER_SCOPE_TOKENS.includes(v))
  );
}

const restrictedToMember = computed(() =>
  (selectedDataSource.value?.row_restriction || []).some(ruleBindsCurrentMember)
);

// 'own' (default) binds to the member's own contact; 'company' widens to every contact
// under their empresa via {{current_member.scoped_contact_ids}} (role-aware server-side).
const memberScope = computed(() => {
  const rule = (selectedDataSource.value?.row_restriction || []).find(
    ruleBindsCurrentMember
  );
  return rule?.values?.includes(MEMBER_COMPANY_TOKEN) ? 'company' : 'own';
});

function toggleMemberRestriction() {
  const ds = selectedDataSource.value;
  if (!ds) return;
  const current = Array.isArray(ds.row_restriction) ? ds.row_restriction : [];
  const next = restrictedToMember.value
    ? current.filter(rule => !ruleBindsCurrentMember(rule))
    : [...current, memberRestrictionRule('own')];
  emit('updateDataSource', { id: ds.id, row_restriction: next });
}

function setMemberScope(scope) {
  const ds = selectedDataSource.value;
  if (!ds) return;
  const current = Array.isArray(ds.row_restriction) ? ds.row_restriction : [];
  const next = [
    ...current.filter(rule => !ruleBindsCurrentMember(rule)),
    memberRestrictionRule(scope),
  ];
  emit('updateDataSource', { id: ds.id, row_restriction: next });
}

const activeTab = ref('data');

const TABS = [
  { key: 'data', labelKey: 'APP_BUILDER.INSPECTOR.TABS.DATA' },
  { key: 'visibility', labelKey: 'APP_BUILDER.INSPECTOR.TABS.VISIBILITY' },
  { key: 'actions', labelKey: 'APP_BUILDER.INSPECTOR.TABS.ACTIONS' },
];

// Named binding slots — each maps a block role to a data-source field key.
const SLOTS = [
  { key: 'title', labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.TITLE' },
  { key: 'subtitle', labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.SUBTITLE' },
  { key: 'status', labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.STATUS' },
  { key: 'date', labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.DATE' },
  { key: 'tag', labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.TAG' },
  {
    key: 'columns',
    labelKey: 'APP_BUILDER.INSPECTOR.SLOTS.COLUMNS',
    multi: true,
  },
];

const isDynamic = computed(() => !!props.block.dynamic);

// The status-only reopen action writes config.action_* keys, which are only schema-safe
// on block types whose config schema is permissive. 'list' has additionalProperties:false
// (layout/page_size/on_click only), so action_* keys there would 422. The detail block
// (item_details) has no strict schema, so actions are allowed there.
const ACTIONABLE_BLOCK_TYPES = ['item_details'];
const isActionable = computed(() =>
  ACTIONABLE_BLOCK_TYPES.includes(props.block.block_type)
);

const dataSourceOptions = computed(() =>
  props.dataSources.map(ds => ({ value: ds.key, label: ds.label || ds.key }))
);

// The entity types a builder can back a data source with. These mirror the server-side
// source-adapter registry (AppData::Sources::Registry); adding an entry here without a
// matching adapter would 422 at create time.
const SOURCE_MODEL_OPTIONS = [
  { value: 'Ticket', labelKey: 'APP_BUILDER.INSPECTOR.SOURCES.TICKET' },
  { value: 'Contract', labelKey: 'APP_BUILDER.INSPECTOR.SOURCES.CONTRACT' },
  {
    value: 'ServiceOrder',
    labelKey: 'APP_BUILDER.INSPECTOR.SOURCES.SERVICE_ORDER',
  },
];
const newSourceModel = ref('Ticket');

function createDataSource() {
  emit('createDataSource', { source_model: newSourceModel.value });
}

function patch(partial) {
  emit('update', { ...props.block, ...partial });
}

// Static-block content lives in `config`. Each static type exposes a small set of
// text fields; quick_links additionally manages an array of { label, url } rows.
function patchConfig(key, value) {
  patch({ config: { ...(props.block.config || {}), [key]: value } });
}

const STATIC_FIELDS = {
  header: ['text', 'subtitle'],
  hero: ['title', 'subtitle', 'image_url'],
  cta: ['text', 'url', 'button_label'],
  quick_links: ['heading'],
};
const staticFields = computed(
  () => STATIC_FIELDS[props.block.block_type] || []
);
const removeGlyph = '×';

function quickLinks() {
  return Array.isArray(props.block.config?.links)
    ? props.block.config.links
    : [];
}
function updateLink(index, key, value) {
  const links = quickLinks().map((l, i) =>
    i === index ? { ...l, [key]: value } : l
  );
  patchConfig('links', links);
}
function addLink() {
  patchConfig('links', [...quickLinks(), { label: '', url: '' }]);
}
function removeLink(index) {
  patchConfig(
    'links',
    quickLinks().filter((_, i) => i !== index)
  );
}

function selectDataSource(e) {
  patch({ data_source_key: e.target.value || null });
}

function selectVisibility(value) {
  patch({ visibility: value });
}

// field_mapping maps slotKey -> fieldKey (or array for multi-value slots).
function slotValue(slotKey) {
  return (
    (props.block.field_mapping || {})[slotKey] ||
    (SLOTS.find(s => s.key === slotKey)?.multi ? [] : '')
  );
}

function setSlotField(slotKey, fieldKey, multi) {
  const mapping = { ...(props.block.field_mapping || {}) };
  if (multi) {
    const current = Array.isArray(mapping[slotKey])
      ? [...mapping[slotKey]]
      : [];
    if (!current.includes(fieldKey)) current.push(fieldKey);
    mapping[slotKey] = current;
  } else {
    mapping[slotKey] = fieldKey;
  }
  patch({ field_mapping: mapping });
}

function clearSlot(slotKey, fieldKey) {
  const mapping = { ...(props.block.field_mapping || {}) };
  if (Array.isArray(mapping[slotKey])) {
    mapping[slotKey] = mapping[slotKey].filter(k => k !== fieldKey);
  } else {
    delete mapping[slotKey];
  }
  patch({ field_mapping: mapping });
}

function fieldLabel(fieldKey) {
  const found = props.metadataFields.find(f => f.key === fieldKey);
  return found ? found.label : fieldKey;
}

function handleFieldDragStart(e, field) {
  e.dataTransfer.setData('app-field-key', field.key);
  e.dataTransfer.effectAllowed = 'copy';
}

function handleSlotDrop(e, slot) {
  const fieldKey = e.dataTransfer.getData('app-field-key');
  if (fieldKey) setSlotField(slot.key, fieldKey, slot.multi);
}

// Actions tab: one-click status update button (status-only reopen).
const actionEnabled = computed(
  () => !!(props.block.config || {}).action_enabled
);

function toggleAction() {
  const config = { ...(props.block.config || {}) };
  config.action_enabled = !config.action_enabled;
  if (config.action_enabled && !config.action_status) {
    config.action_status = 'open';
  }
  patch({ config });
}

function updateActionLabel(e) {
  const config = { ...(props.block.config || {}) };
  config.action_label = e.target.value;
  patch({ config });
}

// Visibility group selection (allowed_group_ids) when restricted to a group.
function toggleGroup(groupId) {
  const ids = [...(props.block.allowed_group_ids || [])];
  const idx = ids.indexOf(groupId);
  if (idx === -1) ids.push(groupId);
  else ids.splice(idx, 1);
  patch({ allowed_group_ids: ids });
}

// Bare enum values — PortalBlock/PortalPage store 'public'/'logged_in'/'group'.
const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    labelKey: 'APP_BUILDER.INSPECTOR.VISIBILITY.PUBLIC',
  },
  {
    value: 'logged_in',
    labelKey: 'APP_BUILDER.INSPECTOR.VISIBILITY.LOGGED_IN',
  },
  {
    value: 'group',
    labelKey: 'APP_BUILDER.INSPECTOR.VISIBILITY.GROUP',
  },
];
</script>

<template>
  <div class="w-72 border-l border-n-weak bg-n-slate-1 shrink-0 flex flex-col">
    <!-- Tabs -->
    <div class="flex border-b border-n-weak">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        class="flex-1 px-2 py-2.5 text-xs font-medium transition-colors"
        :class="
          activeTab === tab.key
            ? 'text-n-slate-12 border-b-2 border-woot-500'
            : 'text-n-slate-10 hover:text-n-slate-11'
        "
        @click="activeTab = tab.key"
      >
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
        {{ t(tab.labelKey) }}
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-3 space-y-4">
      <!-- DATA TAB -->
      <template v-if="activeTab === 'data'">
        <!-- Static blocks: content config (no data binding) -->
        <div v-if="!isDynamic" class="flex flex-col gap-3">
          <div v-for="f in staticFields" :key="f" class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
              {{ t(`APP_BUILDER.INSPECTOR.CONTENT.${f.toUpperCase()}`) }}
            </label>
            <input
              :value="(block.config || {})[f] || ''"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
              @input="patchConfig(f, $event.target.value)"
            />
          </div>

          <template v-if="block.block_type === 'quick_links'">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('APP_BUILDER.INSPECTOR.CONTENT.LINKS') }}
            </label>
            <div
              v-for="(link, i) in quickLinks()"
              :key="i"
              class="flex gap-1 items-center"
            >
              <input
                :value="link.label"
                :placeholder="t('APP_BUILDER.INSPECTOR.CONTENT.LABEL')"
                class="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                @input="updateLink(i, 'label', $event.target.value)"
              />
              <input
                :value="link.url"
                :placeholder="t('APP_BUILDER.INSPECTOR.CONTENT.URL')"
                class="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                @input="updateLink(i, 'url', $event.target.value)"
              />
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-ruby-9 px-1"
                @click="removeLink(i)"
              >
                {{ removeGlyph }}
              </button>
            </div>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline text-left"
              @click="addLink"
            >
              + {{ t('APP_BUILDER.INSPECTOR.CONTENT.ADD_LINK') }}
            </button>
          </template>
        </div>
        <template v-else>
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('APP_BUILDER.INSPECTOR.DATA_SOURCE') }}
            </label>
            <select
              :value="block.data_source_key || ''"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
              @change="selectDataSource"
            >
              <option value="">
                {{ t('APP_BUILDER.INSPECTOR.SELECT_SOURCE') }}
              </option>
              <option
                v-for="opt in dataSourceOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider mt-1"
            >
              {{ t('APP_BUILDER.INSPECTOR.SOURCE_MODEL') }}
            </label>
            <select
              v-model="newSourceModel"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            >
              <option
                v-for="opt in SOURCE_MODEL_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ t(opt.labelKey) }}
              </option>
            </select>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline text-left mt-1"
              @click="createDataSource"
            >
              + {{ t('APP_BUILDER.INSPECTOR.NEW_SOURCE') }}
            </button>
          </div>

          <template v-if="block.data_source_key">
            <!-- Row restriction (the moat): bind the source to the current member -->
            <div
              v-if="selectedDataSource"
              class="flex flex-col gap-1 rounded-lg border border-n-weak p-2.5 bg-n-alpha-1"
            >
              <div class="flex items-center justify-between">
                <label class="text-xs font-medium text-n-slate-12">
                  {{ t('APP_BUILDER.INSPECTOR.RESTRICTION.LABEL') }}
                </label>
                <button
                  class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
                  :class="restrictedToMember ? 'bg-woot-500' : 'bg-n-slate-7'"
                  :aria-label="t('APP_BUILDER.INSPECTOR.RESTRICTION.LABEL')"
                  @click="toggleMemberRestriction"
                >
                  <span
                    class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                    :class="restrictedToMember ? 'left-[17px]' : 'left-[2px]'"
                  />
                </button>
              </div>
              <p class="text-[10px] text-n-slate-9">
                {{ t('APP_BUILDER.INSPECTOR.RESTRICTION.HINT') }}
              </p>

              <!-- Scope: own records vs the member's whole company (role-aware) -->
              <div
                v-if="restrictedToMember"
                class="flex flex-col gap-1 pt-1 mt-1 border-t border-n-weak"
              >
                <label
                  class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('APP_BUILDER.INSPECTOR.SCOPE.LABEL') }}
                </label>
                <select
                  :value="memberScope"
                  class="w-full px-2.5 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                  @change="setMemberScope($event.target.value)"
                >
                  <option value="own">
                    {{ t('APP_BUILDER.INSPECTOR.SCOPE.OWN') }}
                  </option>
                  <option value="company">
                    {{ t('APP_BUILDER.INSPECTOR.SCOPE.COMPANY') }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Available fields (draggable) -->
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ t('APP_BUILDER.INSPECTOR.FIELDS') }}
              </label>
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="field in metadataFields"
                  :key="field.key"
                  class="text-[10px] bg-n-alpha-2 text-n-slate-11 px-1.5 py-1 rounded cursor-grab hover:bg-n-alpha-3"
                  draggable="true"
                  @dragstart="handleFieldDragStart($event, field)"
                >
                  {{ field.label }}
                </span>
                <span
                  v-if="!metadataFields.length"
                  class="text-[10px] text-n-slate-9"
                >
                  {{ t('APP_BUILDER.INSPECTOR.NO_FIELDS') }}
                </span>
              </div>
            </div>

            <!-- Named slots (drop targets) -->
            <div class="flex flex-col gap-2">
              <label
                class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ t('APP_BUILDER.INSPECTOR.SLOTS_TITLE') }}
              </label>
              <div
                v-for="slot in SLOTS"
                :key="slot.key"
                class="rounded-lg border border-dashed border-n-weak p-2"
                @dragover.prevent
                @drop="handleSlotDrop($event, slot)"
              >
                <div class="text-[10px] font-medium text-n-slate-11 mb-1">
                  <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                  {{ t(slot.labelKey) }}
                </div>
                <div v-if="slot.multi" class="flex flex-wrap gap-1">
                  <span
                    v-for="key in slotValue(slot.key)"
                    :key="key"
                    class="inline-flex items-center gap-1 text-[10px] bg-teal-400/10 text-teal-400 px-1.5 py-0.5 rounded"
                  >
                    {{ fieldLabel(key) }}
                    <button
                      class="hover:text-red-400"
                      :aria-label="t('APP_BUILDER.INSPECTOR.REMOVE_FIELD')"
                      @click="clearSlot(slot.key, key)"
                    >
                      <svg
                        class="w-2.5 h-2.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                  <span
                    v-if="!slotValue(slot.key).length"
                    class="text-[10px] text-n-slate-9"
                  >
                    {{ t('APP_BUILDER.INSPECTOR.DROP_HERE') }}
                  </span>
                </div>
                <div v-else>
                  <span
                    v-if="slotValue(slot.key)"
                    class="inline-flex items-center gap-1 text-[10px] bg-teal-400/10 text-teal-400 px-1.5 py-0.5 rounded"
                  >
                    {{ fieldLabel(slotValue(slot.key)) }}
                    <button
                      class="hover:text-red-400"
                      :aria-label="t('APP_BUILDER.INSPECTOR.REMOVE_FIELD')"
                      @click="clearSlot(slot.key)"
                    >
                      <svg
                        class="w-2.5 h-2.5"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                  <span v-else class="text-[10px] text-n-slate-9">
                    {{ t('APP_BUILDER.INSPECTOR.DROP_HERE') }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </template>
      </template>

      <!-- VISIBILITY TAB -->
      <template v-else-if="activeTab === 'visibility'">
        <div class="flex flex-col gap-1.5">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('APP_BUILDER.INSPECTOR.VISIBILITY.LABEL') }}
          </label>
          <button
            v-for="opt in VISIBILITY_OPTIONS"
            :key="opt.value"
            class="w-full text-left px-2.5 py-2 rounded-lg border text-sm transition-colors"
            :class="
              block.visibility === opt.value
                ? 'border-woot-500/40 bg-n-blue-2/40 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-strong'
            "
            @click="selectVisibility(opt.value)"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ t(opt.labelKey) }}
          </button>
        </div>

        <div v-if="block.visibility === 'group'" class="flex flex-col gap-1.5">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('APP_BUILDER.INSPECTOR.VISIBILITY.GROUPS') }}
          </label>
          <label
            v-for="group in memberGroups"
            :key="group.id"
            class="flex items-center gap-2 text-sm text-n-slate-11 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="(block.allowed_group_ids || []).includes(group.id)"
              @change="toggleGroup(group.id)"
            />
            {{ group.label || group.key }}
          </label>
          <span v-if="!memberGroups.length" class="text-[10px] text-n-slate-9">
            {{ t('APP_BUILDER.INSPECTOR.VISIBILITY.NO_GROUPS') }}
          </span>
        </div>
      </template>

      <!-- ACTIONS TAB -->
      <template v-else>
        <div v-if="!isActionable" class="text-xs text-n-slate-10">
          {{ t('APP_BUILDER.INSPECTOR.ACTIONS.DETAIL_ONLY_NOTICE') }}
        </div>
        <template v-else>
          <div class="flex items-center justify-between">
            <label class="text-sm text-n-slate-12">
              {{ t('APP_BUILDER.INSPECTOR.ACTIONS.STATUS_UPDATE') }}
            </label>
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="actionEnabled ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="toggleAction"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="actionEnabled ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
          </div>
          <p class="text-[10px] text-n-slate-9">
            {{ t('APP_BUILDER.INSPECTOR.ACTIONS.STATUS_UPDATE_HINT') }}
          </p>
          <Input
            v-if="actionEnabled"
            :model-value="(block.config || {}).action_label || ''"
            :label="t('APP_BUILDER.INSPECTOR.ACTIONS.BUTTON_LABEL')"
            :placeholder="
              t('APP_BUILDER.INSPECTOR.ACTIONS.BUTTON_LABEL_PLACEHOLDER')
            "
            size="sm"
            @input="updateActionLabel"
          />
        </template>
      </template>
    </div>
  </div>
</template>
